import { CERAMIC_BOOKING_DEPOSIT_CENTS } from './booking-domain.ts'
import {
  parseAdminInvoiceRequest,
  remainingBalanceCents,
  type AdminInvoiceRequest,
} from './xero-reconciliation.ts'

export const JOB_STATUSES = [
  'booked',
  'in_progress',
  'ready_to_invoice',
  'completed',
  'paid',
  'cancelled',
] as const

export type JobStatus = (typeof JOB_STATUSES)[number]

export const SETTLEMENT_STATUSES = [
  'not_ready',
  'invoice_created',
  'deposit_applied',
  'balance_due',
  'payment_pending',
  'paid',
  'failed',
] as const

export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number]

export const SETTLEMENT_SOURCES = [
  'stripe_deposit',
  'square',
  'xero_remote',
  'manual',
] as const

export type SettlementSource = (typeof SETTLEMENT_SOURCES)[number]

export interface FinalizeJobRequest extends AdminInvoiceRequest {}

export interface JobRecord {
  jobStatus: JobStatus
  finalJobAmountCents: number | null
  finalJobCurrency: 'AUD'
  workPerformedSummary: string | null
  completedAt: string | null
}

export interface SettlementSnapshot {
  jobStatus: JobStatus
  settlementStatus: SettlementStatus
  settlementSource: SettlementSource | null
  currency: 'AUD'
  finalJobAmountCents: number | null
  depositPaidCents: number
  depositRequiredCents: number
  invoiceTotalCents: number | null
  amountPaidCents: number
  remainingCents: number
  invoiceId: string | null
  invoiceNumber: string | null
  onlineInvoiceUrl: string | null
  squareConnected: boolean
  squarePaymentPending: boolean
  canFinaliseJob: boolean
  canTakeSquarePayment: boolean
  canRefreshXero: boolean
}

export function parseFinalizeJobRequest(body: unknown) {
  return parseAdminInvoiceRequest(body)
}

export function parseSquareLaunchRequest(body: unknown): {
  ok: true
  bookingId: string
} | {
  ok: false
  error: string
} {
  if (!body || typeof body !== 'object') {
    return { ok: false, error: 'A booking id is required.' }
  }
  const payload = body as Record<string, unknown>
  const bookingId = typeof payload.bookingId === 'string' ? payload.bookingId.trim() : ''
  if (!bookingId) return { ok: false, error: 'A booking id is required.' }
  return { ok: true, bookingId }
}

export function jobStatusAfterFinalise(current: JobStatus | null | undefined): JobStatus {
  if (current === 'cancelled') return 'cancelled'
  if (current === 'paid') return 'paid'
  return 'completed'
}

export function jobStatusAfterSettlementPaid(current: JobStatus | null | undefined): JobStatus {
  if (current === 'cancelled') return 'cancelled'
  return 'paid'
}

export function deriveSettlementStatus(input: {
  failed?: boolean
  invoiceId?: string | null
  amountPaidCents?: number
  remainingCents?: number
  squarePaymentPending?: boolean
}): SettlementStatus {
  if (input.failed) return 'failed'
  if (!input.invoiceId) return 'not_ready'
  if ((input.remainingCents ?? 0) <= 0 && (input.amountPaidCents ?? 0) > 0) return 'paid'
  if (input.squarePaymentPending) return 'payment_pending'
  if ((input.amountPaidCents ?? 0) >= CERAMIC_BOOKING_DEPOSIT_CENTS) {
    return (input.remainingCents ?? 0) > 0 ? 'balance_due' : 'paid'
  }
  if ((input.amountPaidCents ?? 0) > 0) return 'deposit_applied'
  return 'invoice_created'
}

export function xeroSyncStatusFromSettlement(status: SettlementStatus) {
  if (status === 'paid') return 'complete'
  if (status === 'balance_due' || status === 'payment_pending') return 'deposit_applied'
  if (status === 'not_ready') return 'not_ready'
  return status
}

export function customerPaymentCreditCents(grossCents: number, _processorFeeCents?: number) {
  return Math.max(0, Math.round(grossCents))
}

export function stripeDepositBlocksFinalSettlement(paymentStatus?: string | null) {
  return paymentStatus !== 'paid'
}

export function remoteInvoiceAlreadyPaid(remainingCents: number) {
  return remainingCents <= 0
}

export function canLaunchSquareRemainingPayment(input: {
  stripePaymentStatus?: string | null
  remainingCents: number
  squareConnected: boolean
}) {
  if (stripeDepositBlocksFinalSettlement(input.stripePaymentStatus)) {
    return { ok: false as const, reason: 'unpaid_deposit' as const }
  }
  if (!input.squareConnected) {
    return { ok: false as const, reason: 'square_not_connected' as const }
  }
  if (remoteInvoiceAlreadyPaid(input.remainingCents)) {
    return { ok: false as const, reason: 'already_paid' as const }
  }
  if (input.remainingCents <= 0) {
    return { ok: false as const, reason: 'nothing_due' as const }
  }
  return { ok: true as const, amountCents: input.remainingCents }
}

export function buildSettlementSnapshot(input: {
  jobStatus?: string | null
  finalJobAmountCents?: number | null
  workPerformedSummary?: string | null
  completedAt?: string | null
  stripePaymentStatus?: string | null
  depositPaidCents?: number | null
  invoiceId?: string | null
  invoiceNumber?: string | null
  invoiceTotalCents?: number | null
  amountPaidCents?: number | null
  outstandingCents?: number | null
  onlineInvoiceUrl?: string | null
  squareConnected?: boolean
  squarePaymentPending?: boolean
  failed?: boolean
  settlementSource?: string | null
}): SettlementSnapshot {
  const invoiceTotalCents = input.invoiceTotalCents ?? input.finalJobAmountCents ?? null
  const amountPaidCents = input.amountPaidCents
    ?? (input.invoiceTotalCents != null && input.outstandingCents != null
      ? input.invoiceTotalCents - input.outstandingCents
      : (input.depositPaidCents ?? 0))
  const remainingCents = invoiceTotalCents == null
    ? 0
    : remainingBalanceCents(invoiceTotalCents, amountPaidCents)
  const settlementStatus = deriveSettlementStatus({
    failed: input.failed,
    invoiceId: input.invoiceId,
    amountPaidCents,
    remainingCents,
    squarePaymentPending: input.squarePaymentPending,
  })
  const stripePaid = !stripeDepositBlocksFinalSettlement(input.stripePaymentStatus)
  const squareConnected = input.squareConnected === true
  const source = SETTLEMENT_SOURCES.includes(input.settlementSource as SettlementSource)
    ? input.settlementSource as SettlementSource
    : null

  return {
    jobStatus: JOB_STATUSES.includes(input.jobStatus as JobStatus)
      ? input.jobStatus as JobStatus
      : 'booked',
    settlementStatus,
    settlementSource: source,
    currency: 'AUD',
    finalJobAmountCents: input.finalJobAmountCents ?? null,
    depositPaidCents: stripePaid ? CERAMIC_BOOKING_DEPOSIT_CENTS : (input.depositPaidCents ?? 0),
    depositRequiredCents: CERAMIC_BOOKING_DEPOSIT_CENTS,
    invoiceTotalCents,
    amountPaidCents,
    remainingCents,
    invoiceId: input.invoiceId ?? null,
    invoiceNumber: input.invoiceNumber ?? null,
    onlineInvoiceUrl: input.onlineInvoiceUrl ?? null,
    squareConnected,
    squarePaymentPending: input.squarePaymentPending === true,
    canFinaliseJob: stripePaid,
    canTakeSquarePayment: canLaunchSquareRemainingPayment({
      stripePaymentStatus: input.stripePaymentStatus,
      remainingCents,
      squareConnected,
    }).ok,
    canRefreshXero: Boolean(input.invoiceId),
  }
}
